import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  getChatLists,
  getChatSearch,
} from "../../../redux/reducers/ChatReducer/ChatSlice";

import type { Chat } from "../../../redux/reducers/ChatReducer/ChatSliceTypes";
import { filterChats } from "../utils/messageUtils";
import { SEARCH, PAGINATION } from "../constants/messages.constants";
import { UI_TEXT } from "../constants/messages.constants";

interface UseChatListReturn {
  chats: Chat[];
  loading: boolean;
  isLoadingMore: boolean;
  filter: "all" | "unread";
  searchQuery: string;
  showSearch: boolean;
  searchResults: any[];
  searchInputRef: React.RefObject<HTMLInputElement>;
  setFilter: (filter: "all" | "unread") => void;
  setSearchQuery: (query: string) => void;
  setShowSearch: (show: boolean) => void;
  handleFilterChange: (newFilter: "all" | "unread") => void;
  handleSearchMessages: () => Promise<void>;
  clearSearch: () => void;
  loadMoreChats: () => void;
  fetchChatLists: () => Promise<void>;
}

/**
 * Custom hook for chat list management
 * Handles fetching, filtering, searching, and pagination of chats
 */
export const useChatList = (): UseChatListReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const chatLists = useSelector((state: RootState) => state.chat.chatLists);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [chatPage, setChatPage] = useState(1);

  /**
   * Fetch chat lists
   */
  const fetchChatLists = async () => {
    try {
      await dispatch(getChatLists(chatPage));
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  /**
   * Load more chats (pagination)
   */
  const loadMoreChats = () => {
    if (
      chatLists?.meta?.total_pages &&
      chatPage < chatLists.meta.total_pages &&
      !loading &&
      !isLoadingMore
    ) {
      setIsLoadingMore(true);
      setChatPage((prev) => prev + 1);
    }
  };

  /**
   * Auto-focus search when opened
   */
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  /**
   * Fetch chats when page changes
   */
  useEffect(() => {
    if (
      chatLists?.meta?.total_pages &&
      chatPage <= chatLists.meta.total_pages
    ) {
      const fetchData = async () => {
        setIsLoadingMore(true);
        await fetchChatLists();
        setIsLoadingMore(false);
      };
      fetchData();
    }
  }, [chatPage]);

  /**
   * Reset to first page when search is cleared
   */
  useEffect(() => {
    if (searchQuery.length === 0) {
      const fetchChatListInitially = async () => {
        await dispatch(getChatLists(chatPage));
      };
      fetchChatListInitially();
    }
  }, [searchQuery]);

  /**
   * Debounced search
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= SEARCH.MIN_SEARCH_LENGTH) {
        handleSearchMessages();
      }
    }, SEARCH.DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  /**
   * Handle search
   */
  const handleSearchMessages = async () => {
    setLoading(true);
    if (!searchQuery.trim()) {
      toast.info(UI_TEXT.ERRORS.SEARCH_EMPTY);
      setLoading(false);
      return;
    }
    try {
      const response = await dispatch(getChatSearch(searchQuery)).unwrap();
      setSearchResults(response);
    } catch (error) {
      console.error("Search error:", error);
      toast.error(UI_TEXT.ERRORS.SEARCH_FAILED);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear search and refocus input
   */
  const clearSearch = () => {
    setSearchQuery("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (newFilter: "all" | "unread") => {
    setFilter(newFilter);
  };

  /**
   * Get filtered chats
   */
  const getFilteredChats = () => {
    return filterChats(chatLists?.chats || [], filter);
  };

  return {
    chats: getFilteredChats(),
    loading,
    isLoadingMore,
    filter,
    searchQuery,
    showSearch,
    searchResults,
    searchInputRef,
    setFilter,
    setSearchQuery,
    setShowSearch,
    handleFilterChange,
    handleSearchMessages,
    clearSearch,
    loadMoreChats,
    fetchChatLists,
  };
};
